import type {ColorThemePreset} from '@core/constants/colorThemePresets';
import type {PresetMonacoDefinition} from './mergeMonacoPresetTheme';

/** Neutral bases; preset canvas is a subtle blend so themes stay distinct. */
const NEUTRAL_LIGHT_EDITOR_BG = '#ffffff';
const NEUTRAL_DARK_EDITOR_BG = '#1e1e1e';

/** How much of the color preset’s UI background is mixed in (0 = flat neutral, 1 = full preset bg). */
const LIGHT_EDITOR_TINT = 0.2;
const DARK_EDITOR_TINT = 0.24;

const BODY_TEXT_LIGHT_ON_LIGHT = '#0F172A';
const BODY_TEXT_LIGHT_ON_DARK = '#E2E8F0';

/** Monaco token rule foreground values omit `#`. */
function stripHash(hex: string): string {
  return hex.replace(/^#/, '');
}

const FALLBACK_RGB = {r: 0, g: 0, b: 0};

function parseRgb(hex: string): {r: number; g: number; b: number} {
  const raw = hex.replace('#', '').trim();
  const n =
    raw.length === 3
      ? raw
          .split('')
          .map(c => c + c)
          .join('')
      : raw;
  const r = Number.parseInt(n.slice(0, 2), 16);
  const g = Number.parseInt(n.slice(2, 4), 16);
  const b = Number.parseInt(n.slice(4, 6), 16);
  if ([r, g, b].some(c => Number.isNaN(c))) {
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
      console.warn(
        '[buildMonacoThemeFromColorPreset] Invalid hex color, using fallback black:',
        hex,
      );
    }
    return {...FALLBACK_RGB};
  }
  return {r, g, b};
}

function toHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mixHex(a: string, b: string, t: number): string {
  const tt = Math.min(1, Math.max(0, t));
  const A = parseRgb(a);
  const B = parseRgb(b);
  return toHex(
    A.r + (B.r - A.r) * tt,
    A.g + (B.g - A.g) * tt,
    A.b + (B.b - A.b) * tt,
  );
}

function relativeLuminance(hex: string): number {
  const {r, g, b} = parseRgb(hex);
  const rs = r / 255;
  const gs = g / 255;
  const bs = b / 255;
  const lin = (c: number) =>
    c <= 0.039_28 ? c / 12.92 : (c + 0.055) ** 2.4 / 1.055 ** 2.4;
  return 0.2126 * lin(rs) + 0.7152 * lin(gs) + 0.0722 * lin(bs);
}

/** Default text color that contrasts with this editor background. */
function bodyTextForEditorBackground(editorBg: string): string {
  return relativeLuminance(editorBg) > 0.5
    ? BODY_TEXT_LIGHT_ON_LIGHT
    : BODY_TEXT_LIGHT_ON_DARK;
}

/**
 * Keeps accent tokens readable on the actual editor surface (light-on-light / dark-on-dark).
 */
function tokenReadableOnEditorBg(hex: string, editorBg: string): string {
  const lumBg = relativeLuminance(editorBg);
  const lumFg = relativeLuminance(hex);
  const lightSurface = lumBg > 0.5;
  if (lightSurface) {
    if (lumFg > 0.52) {
      return mixHex(hex, '#111827', 0.82);
    }
    return hex;
  }
  if (lumFg < 0.38) {
    return mixHex(hex, '#E2E8F0', 0.72);
  }
  return hex;
}

/**
 * Colour presets often reuse the same primary/secondary/accent hex for `light` and `dark` UI
 * (only page background differs). After contrast fixes, syntax tokens can still look identical.
 * For dark editor themes, lift tokens slightly toward a cool light so they read clearly and
 * differ from the light-mode Monaco palette.
 */
const DARK_SYNTAX_LIFT_TARGET = '#E8EDF5';
const DARK_SYNTAX_LIFT = 0.18;

function finalizeSyntaxTokenColor(
  sourceHex: string,
  editorBg: string,
  mode: 'light' | 'dark',
): string {
  const toned = tokenReadableOnEditorBg(sourceHex, editorBg);
  if (mode === 'light') {
    return toned;
  }
  return mixHex(toned, DARK_SYNTAX_LIFT_TARGET, DARK_SYNTAX_LIFT);
}

function lineNumberMuted(mode: 'light' | 'dark'): string {
  return mode === 'light' ? '#64748B' : '#94A3B8';
}

/**
 * Builds a Monaco preset theme: subtly tinted editor surface (per preset) +
 * preset colors in syntax tokens (contrast-adjusted to that surface).
 */
export function createPresetMonacoDefinitionFromColorPreset(
  preset: ColorThemePreset,
  mode: 'light' | 'dark',
): PresetMonacoDefinition {
  const palette = mode === 'light' ? preset.light : preset.dark;
  const {primary, secondary, accent, background: presetSurfaceBg} = palette;
  const {status} = preset;
  const base: 'vs' | 'vs-dark' = mode === 'light' ? 'vs' : 'vs-dark';
  const id =
    mode === 'light' ? `preset-${preset.id}` : `preset-${preset.id}-dark`;

  const backgroundColor =
    mode === 'light'
      ? mixHex(NEUTRAL_LIGHT_EDITOR_BG, presetSurfaceBg, LIGHT_EDITOR_TINT)
      : mixHex(NEUTRAL_DARK_EDITOR_BG, presetSurfaceBg, DARK_EDITOR_TINT);

  const foregroundColor = bodyTextForEditorBackground(backgroundColor);
  const ln = lineNumberMuted(mode);

  const tok = (hex: string) =>
    finalizeSyntaxTokenColor(hex, backgroundColor, mode);

  const rules: PresetMonacoDefinition['rules'] = [
    {token: 'comment', foreground: stripHash(ln), fontStyle: 'italic'},
    {token: 'string', foreground: stripHash(tok(primary))},
    {token: 'string.key.json', foreground: stripHash(tok(secondary))},
    {token: 'number', foreground: stripHash(tok(accent))},
    {token: 'keyword', foreground: stripHash(tok(status.error))},
    {token: 'keyword.json', foreground: stripHash(tok(secondary))},
    {token: 'delimiter', foreground: stripHash(ln)},
    {token: 'delimiter.bracket.json', foreground: stripHash(foregroundColor)},
    {token: 'operator', foreground: stripHash(ln)},
    {token: 'type', foreground: stripHash(tok(accent))},
    {token: 'identifier', foreground: stripHash(foregroundColor)},
    {token: 'regexp', foreground: stripHash(tok(status.warning))},
  ];

  return {
    id,
    base,
    backgroundColor,
    foregroundColor,
    colors: {
      'editorLineNumber.foreground': ln,
    },
    rules,
  };
}

/** One light + one dark Monaco theme per color preset. */
export function createAllPresetMonacoDefinitionsFromColorPresets(
  presets: readonly ColorThemePreset[],
): PresetMonacoDefinition[] {
  return presets.flatMap(p => [
    createPresetMonacoDefinitionFromColorPreset(p, 'light'),
    createPresetMonacoDefinitionFromColorPreset(p, 'dark'),
  ]);
}
